// 100ms Hook using vanilla hms-video-store (React 19 compatible)
import { useCallback, useEffect, useState, useRef } from 'react';
import { 
  HMSReactiveStore,
  selectIsConnectedToRoom,
  selectPeers,
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
  selectHMSMessages,
  selectLocalPeer,
  selectConnectionQualityByPeerID,
  selectLocalPeerID
} from '@100mslive/hms-video-store';
import axios from 'axios';
import { API } from '../App';

// Create a singleton store instance - shared across all hook instances
let hmsStore = null;
let hmsActions = null;
let hmsInitialized = false;

function getHMSInstance() {
  if (!hmsInitialized) {
    const hms = new HMSReactiveStore();
    hmsStore = hms.getStore();
    hmsActions = hms.getActions();
    hmsInitialized = true;
  }
  return { hmsStore, hmsActions };
}

export function useHMS(sessionId) {
  const { hmsStore, hmsActions } = getHMSInstance();
  
  // Initialize states with current store values
  const [isConnected, setIsConnected] = useState(() => hmsStore.getState(selectIsConnectedToRoom));
  const [peers, setPeers] = useState(() => hmsStore.getState(selectPeers) || []);
  const [localPeer, setLocalPeer] = useState(() => hmsStore.getState(selectLocalPeer));
  const [isAudioEnabled, setIsAudioEnabled] = useState(() => hmsStore.getState(selectIsLocalAudioEnabled));
  const [isVideoEnabled, setIsVideoEnabled] = useState(() => hmsStore.getState(selectIsLocalVideoEnabled));
  const [messages, setMessages] = useState(() => hmsStore.getState(selectHMSMessages) || []);
  const [connectionQuality, setConnectionQuality] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [hmsConfigured, setHmsConfigured] = useState(null);
  
  // Track if this hook instance initiated the join
  const isJoiningRef = useRef(false);
  const hasJoinedRef = useRef(false);
  const [localPeerID, setLocalPeerID] = useState(() => hmsStore.getState(selectLocalPeerID));

  // Subscribe to store changes
  useEffect(() => {
    const unsubscribes = [];
    
    unsubscribes.push(
      hmsStore.subscribe((connected) => {
        console.log('HMS Store: isConnected subscription fired with value:', connected);
        setIsConnected(connected);
        if (connected) {
          hasJoinedRef.current = true;
          isJoiningRef.current = false;
        }
      }, selectIsConnectedToRoom)
    );
    unsubscribes.push(
      hmsStore.subscribe(setPeers, selectPeers)
    );
    unsubscribes.push(
      hmsStore.subscribe(setLocalPeer, selectLocalPeer)
    );
    unsubscribes.push(
      hmsStore.subscribe(setLocalPeerID, selectLocalPeerID)
    );
    unsubscribes.push(
      hmsStore.subscribe(setIsAudioEnabled, selectIsLocalAudioEnabled)
    );
    unsubscribes.push(
      hmsStore.subscribe(setIsVideoEnabled, selectIsLocalVideoEnabled)
    );
    unsubscribes.push(
      hmsStore.subscribe(setMessages, selectHMSMessages)
    );
    
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [hmsStore]);

  // Subscribe to connection quality when we have localPeerID
  useEffect(() => {
    if (!localPeerID) return;
    
    const unsub = hmsStore.subscribe((quality) => {
      setConnectionQuality(quality);
    }, selectConnectionQualityByPeerID(localPeerID));
    
    return () => unsub();
  }, [hmsStore, localPeerID]);

  // Check if 100ms is configured
  useEffect(() => {
    checkHMSConfig();
  }, []);

  const checkHMSConfig = async () => {
    try {
      const response = await axios.get(`${API}/hms/config`);
      setHmsConfigured(response.data.configured);
    } catch (err) {
      setHmsConfigured(false);
    }
  };

  // Create or get room for session
  const createRoom = useCallback(async (channel = 'chat') => {
    if (!sessionId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/hms/create-room`,
        { session_id: sessionId, channel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRoomId(response.data.room_id);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create room');
      return null;
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // Get token and join room
  const joinRoom = useCallback(async (hmsRoomId, options = {}) => {
    if (!sessionId || !hmsRoomId) {
      console.log('joinRoom: missing sessionId or hmsRoomId');
      return false;
    }
    
    // Prevent double-join attempts
    if (isJoiningRef.current || hasJoinedRef.current) {
      console.log('joinRoom: already joining or joined, skipping');
      return hasJoinedRef.current;
    }
    
    isJoiningRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/hms/token`,
        { session_id: sessionId, hms_room_id: hmsRoomId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { token: authToken, display_name: userName } = response.data;
      
      // Join 100ms room
      console.log('Attempting to join 100ms room with token:', authToken.substring(0, 50) + '...');
      console.log('Username:', userName);
      
      await hmsActions.join({
        userName,
        authToken,
        settings: {
          isAudioMuted: options.audioMuted ?? true,
          isVideoMuted: options.videoMuted ?? true
        }
      });
      
      console.log('Join call completed successfully');
      // The isConnected state will be updated by the subscription
      // Wait a moment for the store to sync
      await new Promise(resolve => setTimeout(resolve, 500));
      return true;
    } catch (err) {
      console.error('joinRoom error:', err);
      isJoiningRef.current = false;
      setError(err.response?.data?.detail || err.message || 'Failed to join room');
      return false;
    } finally {
      setLoading(false);
    }
  }, [sessionId, hmsActions]);

  // Create room and join in one step
  const createAndJoin = useCallback(async (channel = 'chat', options = {}) => {
    const roomData = await createRoom(channel);
    if (roomData?.room_id) {
      return await joinRoom(roomData.room_id, options);
    }
    return false;
  }, [createRoom, joinRoom]);

  // Leave room
  const leaveRoom = useCallback(async () => {
    try {
      await hmsActions.leave();
      hasJoinedRef.current = false;
      isJoiningRef.current = false;
      return true;
    } catch (err) {
      console.error('Error leaving room:', err);
      return false;
    }
  }, [hmsActions]);

  // Toggle audio
  const toggleAudio = useCallback(async () => {
    try {
      await hmsActions.setLocalAudioEnabled(!isAudioEnabled);
    } catch (err) {
      console.error('Error toggling audio:', err);
    }
  }, [isAudioEnabled]);

  // Toggle video
  const toggleVideo = useCallback(async () => {
    try {
      await hmsActions.setLocalVideoEnabled(!isVideoEnabled);
    } catch (err) {
      console.error('Error toggling video:', err);
    }
  }, [isVideoEnabled]);

  // Send chat message
  const sendMessage = useCallback(async (message) => {
    try {
      await hmsActions.sendBroadcastMessage(message);
      return true;
    } catch (err) {
      console.error('Error sending message:', err);
      return false;
    }
  }, []);

  // End room (provider only)
  const endRoom = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/hms/end-room/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await hmsActions.leave();
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to end room');
      return false;
    }
  }, [sessionId]);

  // Attach video to element
  const attachVideo = useCallback((trackId, element) => {
    if (trackId && element) {
      hmsActions.attachVideo(trackId, element);
    }
  }, []);

  // Detach video from element
  const detachVideo = useCallback((trackId, element) => {
    if (trackId && element) {
      hmsActions.detachVideo(trackId, element);
    }
  }, []);

  // Cleanup on unmount only - NOT on isConnected change
  useEffect(() => {
    return () => {
      // Only leave if this component actually joined
      if (hasJoinedRef.current) {
        console.log('useHMS cleanup: leaving room on unmount');
        hmsActions.leave().catch(console.error);
        hasJoinedRef.current = false;
        isJoiningRef.current = false;
      }
    };
  }, [hmsActions]);

  return {
    // State
    isConnected,
    isAudioEnabled,
    isVideoEnabled,
    peers,
    localPeer,
    messages,
    roomId,
    loading,
    error,
    hmsConfigured,
    connectionQuality,
    
    // Actions
    createRoom,
    joinRoom,
    createAndJoin,
    leaveRoom,
    toggleAudio,
    toggleVideo,
    sendMessage,
    endRoom,
    attachVideo,
    detachVideo,
    checkHMSConfig,
    
    // Store access for advanced use
    hmsStore,
    hmsActions
  };
}

export default useHMS;
